import type { VisualShape } from 'types'
import type { ParticleMaterial } from './types'

export type MorphStyle = 'dissolve' | 'flow' | 'spiral'

export interface ShapePolishProfile {
  alpha: number
  depth: number
  bloom: number
  noise: number
  spring: number
  chatShift: number
  chatScale: number
  fullBleed?: boolean
}

export interface ShapeRenderSpec {
  persistentTrail: boolean
  structuralLines: boolean
  material?: ParticleMaterial
}

export interface ShapeMorphSpec {
  defaultStyle: MorphStyle
  durationMs: number
}

export interface ShapeThemeSpec {
  hueShift?: number
  bloomIntensity?: number
}

export interface ShapeDesignSpec {
  polish: ShapePolishProfile
  render: ShapeRenderSpec
  morph: ShapeMorphSpec
  theme?: ShapeThemeSpec
}

export const DEFAULT_MORPH_DURATION_MS = 600
export const ERROR_MORPH_DURATION_MS = 400
export const BRAND_COPPER_RGB: [number, number, number] = [201, 95, 58]

/** CSS `ease-out` — cubic-bezier(0, 0, 0.58, 1). Default 600ms morph timing. */
export const MORPH_EASE_OUT = { x1: 0, y1: 0, x2: 0.58, y2: 1 } as const
/** Stronger ease-out for spiral returns to orb. */
export const MORPH_EASE_OUT_SPIRAL = { x1: 0.16, y1: 1, x2: 0.3, y2: 1 } as const
/** Ease-out with a slower start so dissolve doesn't pop. */
export const MORPH_EASE_OUT_DISSOLVE = { x1: 0.22, y1: 0, x2: 0.36, y2: 1 } as const

const PERSISTENT_TRAIL_SHAPES = new Set<VisualShape>([
  'wave',
  'dna',
  'grid',
  'globe',
  'brain',
  'constellation',
  'network',
  'molecule',
  'neural_network',
  'weather',
  'chart',
  'map',
  'clock',
  'code',
  'workshop',
  'consulting',
  'proposal',
  'summary',
])

const STRUCTURAL_LINE_SHAPES = new Set<VisualShape>([
  'constellation',
  'network',
  'neural_network',
  'brain',
  'workshop',
  'consulting',
  'proposal',
  'screenshare',
  'webcam',
  'atom',
  'molecule',
  'solarSystem',
  'chart',
  'grid',
  'map',
  'clock',
  'shield',
  'summary',
  'toolCall',
  'functionPulse',
])

const DISSOLVE_TARGETS = new Set<VisualShape>(['grid', 'proposal', 'scoring'])
const SPIRAL_TARGETS = new Set<VisualShape>(['orb', 'vortex', 'summary', 'discovery'])

const DEFAULT_PROFILE: ShapePolishProfile = {
  alpha: 1,
  depth: 1,
  bloom: 0.22,
  noise: 0.42,
  spring: 0.92,
  chatShift: 0.72,
  chatScale: 0.88,
}

const SHAPE_PROFILES: Record<VisualShape, ShapePolishProfile> = {
  orb: { alpha: 1, depth: 1.12, bloom: 0.28, noise: 0.32, spring: 0.88, chatShift: 0.8, chatScale: 0.82 },
  wave: { alpha: 0.9, depth: 0.92, bloom: 0.28, noise: 0.76, spring: 0.92, chatShift: 0.42, chatScale: 0.78, fullBleed: true },
  dna: { alpha: 1, depth: 1.08, bloom: 0.24, noise: 0.28, spring: 0.9, chatShift: 0.82, chatScale: 0.8 },
  rect: { alpha: 0.78, depth: 0.86, bloom: 0.12, noise: 0.24, spring: 0.9, chatShift: 0.78, chatScale: 0.78 },
  planet: { alpha: 0.98, depth: 1.16, bloom: 0.26, noise: 0.28, spring: 0.88, chatShift: 0.84, chatScale: 0.74 },
  heart: { alpha: 0.88, depth: 0.98, bloom: 0.18, noise: 0.3, spring: 0.9, chatShift: 0.78, chatScale: 0.74 },
  grid: { alpha: 0.64, depth: 0.78, bloom: 0.08, noise: 0.12, spring: 0.86, chatShift: 0.72, chatScale: 0.68, fullBleed: true },
  atom: { alpha: 1, depth: 1.08, bloom: 0.24, noise: 0.28, spring: 0.9, chatShift: 0.84, chatScale: 0.72 },
  hourglass: { alpha: 0.86, depth: 0.96, bloom: 0.16, noise: 0.24, spring: 0.88, chatShift: 0.8, chatScale: 0.74 },
  shield: { alpha: 0.9, depth: 1.02, bloom: 0.16, noise: 0.18, spring: 0.9, chatShift: 0.82, chatScale: 0.72 },
  star: { alpha: 0.92, depth: 1.04, bloom: 0.22, noise: 0.26, spring: 0.9, chatShift: 0.8, chatScale: 0.74 },
  globe: { alpha: 0.94, depth: 1.1, bloom: 0.2, noise: 0.26, spring: 0.88, chatShift: 0.82, chatScale: 0.72 },
  brain: { alpha: 0.96, depth: 1.08, bloom: 0.22, noise: 0.26, spring: 0.88, chatShift: 0.82, chatScale: 0.76 },
  constellation: { alpha: 0.96, depth: 1.06, bloom: 0.22, noise: 0.18, spring: 0.86, chatShift: 0.84, chatScale: 0.78 },
  weather: { alpha: 0.9, depth: 0.96, bloom: 0.16, noise: 0.24, spring: 0.88, chatShift: 0.76, chatScale: 0.74 },
  chart: { alpha: 0.88, depth: 0.94, bloom: 0.12, noise: 0.18, spring: 0.94, chatShift: 0.78, chatScale: 0.76 },
  map: { alpha: 0.94, depth: 1.08, bloom: 0.18, noise: 0.22, spring: 0.88, chatShift: 0.82, chatScale: 0.72 },
  clock: { alpha: 0.82, depth: 0.94, bloom: 0.12, noise: 0.16, spring: 0.86, chatShift: 0.78, chatScale: 0.72 },
  code: { alpha: 0.8, depth: 0.86, bloom: 0.1, noise: 0.14, spring: 0.94, chatShift: 0.66, chatScale: 0.7, fullBleed: true },
  text: { alpha: 0.96, depth: 0.98, bloom: 0.18, noise: 0.2, spring: 0.96, chatShift: 0.82, chatScale: 0.74 },
  scanner: { alpha: 0.82, depth: 0.9, bloom: 0.18, noise: 0.16, spring: 0.94, chatShift: 0.62, chatScale: 0.76, fullBleed: true },
  vortex: { alpha: 0.96, depth: 1.12, bloom: 0.22, noise: 0.26, spring: 0.88, chatShift: 0.84, chatScale: 0.74 },
  fireworks: { alpha: 0.78, depth: 1.02, bloom: 0.2, noise: 0.22, spring: 0.92, chatShift: 0.74, chatScale: 0.76 },
  lightning: { alpha: 0.86, depth: 1, bloom: 0.22, noise: 0.2, spring: 0.94, chatShift: 0.7, chatScale: 0.78 },
  flower: { alpha: 0.92, depth: 1.02, bloom: 0.18, noise: 0.26, spring: 0.88, chatShift: 0.82, chatScale: 0.74 },
  discovery: { alpha: 0.96, depth: 1.04, bloom: 0.22, noise: 0.26, spring: 0.88, chatShift: 0.84, chatScale: 0.78 },
  scoring: { alpha: 0.9, depth: 0.94, bloom: 0.14, noise: 0.16, spring: 0.94, chatShift: 0.78, chatScale: 0.76 },
  workshop: { alpha: 0.98, depth: 1.06, bloom: 0.22, noise: 0.22, spring: 0.88, chatShift: 0.84, chatScale: 0.78 },
  consulting: { alpha: 0.96, depth: 1.06, bloom: 0.2, noise: 0.2, spring: 0.86, chatShift: 0.84, chatScale: 0.78 },
  closer: { alpha: 0.94, depth: 1.04, bloom: 0.2, noise: 0.24, spring: 0.92, chatShift: 0.82, chatScale: 0.74 },
  summary: { alpha: 0.9, depth: 1, bloom: 0.16, noise: 0.2, spring: 0.88, chatShift: 0.8, chatScale: 0.76 },
  proposal: { alpha: 0.86, depth: 0.92, bloom: 0.12, noise: 0.14, spring: 0.9, chatShift: 0.76, chatScale: 0.74 },
  admin: { alpha: 0.84, depth: 0.96, bloom: 0.14, noise: 0.18, spring: 0.9, chatShift: 0.78, chatScale: 0.74 },
  retargeting: { alpha: 0.88, depth: 1, bloom: 0.18, noise: 0.24, spring: 0.88, chatShift: 0.82, chatScale: 0.76 },
  oscilloscope: { alpha: 0.9, depth: 0.86, bloom: 0.16, noise: 0.18, spring: 0.94, chatShift: 0.46, chatScale: 0.68, fullBleed: true },
  spectrum: { alpha: 0.86, depth: 0.86, bloom: 0.14, noise: 0.18, spring: 0.92, chatShift: 0.48, chatScale: 0.68, fullBleed: true },
  toolCall: { alpha: 0.96, depth: 1.04, bloom: 0.22, noise: 0.2, spring: 0.92, chatShift: 0.82, chatScale: 0.72 },
  functionPulse: { alpha: 0.92, depth: 1.02, bloom: 0.2, noise: 0.2, spring: 0.9, chatShift: 0.82, chatScale: 0.74 },
  solarSystem: { alpha: 0.96, depth: 1.12, bloom: 0.22, noise: 0.26, spring: 0.88, chatShift: 0.84, chatScale: 0.7 },
  network: { alpha: 0.92, depth: 0.98, bloom: 0.18, noise: 0.18, spring: 0.88, chatShift: 0.78, chatScale: 0.74 },
  molecule: { alpha: 0.96, depth: 1.04, bloom: 0.18, noise: 0.18, spring: 0.88, chatShift: 0.82, chatScale: 0.72 },
  mathGraph: { alpha: 0.88, depth: 0.94, bloom: 0.14, noise: 0.16, spring: 0.9, chatShift: 0.68, chatScale: 0.72, fullBleed: true },
  neural_network: { alpha: 0.94, depth: 1.04, bloom: 0.2, noise: 0.18, spring: 0.88, chatShift: 0.78, chatScale: 0.74 },
  error: { alpha: 0.94, depth: 1.04, bloom: 0.28, noise: 0.3, spring: 0.94, chatShift: 0.82, chatScale: 0.72 },
  screenshare: { alpha: 0.9, depth: 0.92, bloom: 0.14, noise: 0.14, spring: 0.9, chatShift: 0.78, chatScale: 0.74 },
  webcam: { alpha: 0.96, depth: 1.02, bloom: 0.18, noise: 0.18, spring: 0.9, chatShift: 0.82, chatScale: 0.72 },
}

function inferDefaultStyle(shape: VisualShape): MorphStyle {
  if (DISSOLVE_TARGETS.has(shape)) return 'dissolve'
  if (SPIRAL_TARGETS.has(shape)) return 'spiral'
  return 'flow'
}

function buildSpec(shape: VisualShape): ShapeDesignSpec {
  return {
    polish: SHAPE_PROFILES[shape] ?? DEFAULT_PROFILE,
    render: {
      persistentTrail: PERSISTENT_TRAIL_SHAPES.has(shape),
      structuralLines: STRUCTURAL_LINE_SHAPES.has(shape),
    },
    morph: {
      defaultStyle: inferDefaultStyle(shape),
      durationMs: shape === 'error' ? ERROR_MORPH_DURATION_MS : DEFAULT_MORPH_DURATION_MS,
    },
    ...(shape === 'error' ? { theme: { bloomIntensity: 0.9 } } : {}),
  }
}

export const SHAPE_DESIGN_SPECS: Record<VisualShape, ShapeDesignSpec> = {
  orb: buildSpec('orb'),
  wave: buildSpec('wave'),
  dna: buildSpec('dna'),
  rect: buildSpec('rect'),
  planet: buildSpec('planet'),
  heart: buildSpec('heart'),
  grid: buildSpec('grid'),
  atom: buildSpec('atom'),
  hourglass: buildSpec('hourglass'),
  shield: buildSpec('shield'),
  star: buildSpec('star'),
  globe: buildSpec('globe'),
  brain: buildSpec('brain'),
  constellation: buildSpec('constellation'),
  weather: buildSpec('weather'),
  chart: buildSpec('chart'),
  map: buildSpec('map'),
  clock: buildSpec('clock'),
  code: buildSpec('code'),
  text: buildSpec('text'),
  scanner: buildSpec('scanner'),
  vortex: buildSpec('vortex'),
  fireworks: buildSpec('fireworks'),
  lightning: buildSpec('lightning'),
  flower: buildSpec('flower'),
  discovery: buildSpec('discovery'),
  scoring: buildSpec('scoring'),
  workshop: buildSpec('workshop'),
  consulting: buildSpec('consulting'),
  closer: buildSpec('closer'),
  summary: buildSpec('summary'),
  proposal: buildSpec('proposal'),
  admin: buildSpec('admin'),
  retargeting: buildSpec('retargeting'),
  oscilloscope: buildSpec('oscilloscope'),
  spectrum: buildSpec('spectrum'),
  toolCall: buildSpec('toolCall'),
  functionPulse: buildSpec('functionPulse'),
  solarSystem: buildSpec('solarSystem'),
  network: buildSpec('network'),
  molecule: buildSpec('molecule'),
  mathGraph: buildSpec('mathGraph'),
  neural_network: buildSpec('neural_network'),
  error: buildSpec('error'),
  screenshare: buildSpec('screenshare'),
  webcam: buildSpec('webcam'),
}

export function getShapeDesignSpec(shape: VisualShape): ShapeDesignSpec {
  return SHAPE_DESIGN_SPECS[shape]
}

export function hasPersistentTrail(shape: VisualShape): boolean {
  return getShapeDesignSpec(shape).render.persistentTrail
}

export function hasStructuralLines(shape: VisualShape): boolean {
  return getShapeDesignSpec(shape).render.structuralLines
}

export function resolveMorphTransition(
  from: VisualShape,
  to: VisualShape,
): ShapeMorphSpec {
  if (from === to) {
    return { defaultStyle: getShapeDesignSpec(to).morph.defaultStyle, durationMs: 0 }
  }
  if (to === 'error') return { defaultStyle: 'dissolve', durationMs: ERROR_MORPH_DURATION_MS }
  if (to === 'orb') return { defaultStyle: 'spiral', durationMs: DEFAULT_MORPH_DURATION_MS }
  if (to === 'discovery' || to === 'summary') {
    return { defaultStyle: 'spiral', durationMs: DEFAULT_MORPH_DURATION_MS }
  }
  if (to === 'grid' || to === 'proposal' || to === 'scoring') {
    return { defaultStyle: 'dissolve', durationMs: DEFAULT_MORPH_DURATION_MS }
  }
  if (
    (from === 'orb' && (to === 'wave' || to === 'webcam')) ||
    (from === 'network' && (to === 'molecule' || to === 'neural_network'))
  ) {
    return { defaultStyle: 'flow', durationMs: DEFAULT_MORPH_DURATION_MS }
  }
  return getShapeDesignSpec(to).morph
}

export function getMorphDurationMs(from: VisualShape, to: VisualShape): number {
  return resolveMorphTransition(from, to).durationMs
}
